import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { DialogRoot, DialogTrigger, Tooltip } from '@gitbook/spine-react';

import { apiClient } from '../../../state';
import {
    QuickActionCard,
    QuickActionCardHeading,
    QuickActionCardIcon,
} from '../../Onboarding/cards';
import {
    CreateTranslationDialog,
    type CreateTranslationDialogProps,
    getTranslatedSpaceTitle,
} from '../../Translations';
import type { SpaceCreationOnCreateSpace } from './types';

interface TranslationCardProps extends Pick<CreateTranslationDialogProps, 'defaultSpace'> {
    organizationId: string;
    onCreateSpace: SpaceCreationOnCreateSpace;
    defaultOpen?: boolean;
    disabled?: boolean;
    disabledReason?: string;
}

export function TranslationCard(props: TranslationCardProps) {
    const { organizationId, onCreateSpace, defaultOpen, defaultSpace, disabled, disabledReason } =
        props;

    const card = (
        <QuickActionCard disabled={disabled}>
            <QuickActionCardIcon>
                <Icon.AITranslate className="transition-transform ease-snappy-out group-hover/QAcard:scale-105 group-focus/QAcard:scale-105" />
            </QuickActionCardIcon>
            <QuickActionCardHeading>Translation</QuickActionCardHeading>
        </QuickActionCard>
    );

    if (disabled) {
        return disabledReason ? <Tooltip tooltip={disabledReason}>{card}</Tooltip> : card;
    }

    return (
        <DialogRoot defaultOpen={defaultOpen}>
            <DialogTrigger>{card}</DialogTrigger>
            <CreateTranslationDialog
                defaultSpace={defaultSpace}
                organizationId={organizationId}
                onSubmit={async ({ sourceSpace, translation }) => {
                    await onCreateSpace(async ({ collectionId }) => {
                        const title = getTranslatedSpaceTitle(
                            sourceSpace.title,
                            translation.language
                        );
                        const { data: space } = await apiClient.spaces.createSpace(organizationId, {
                            // Keep translated spaces next to their source by default.
                            parent: collectionId ?? sourceSpace.parent ?? undefined,
                            title,
                            language: translation.language,
                            computedSource: {
                                type: 'builtin:translation',
                                props: {},
                                dependencies: {
                                    translation: {
                                        ref: {
                                            kind: 'translation',
                                            translation: translation.id,
                                        },
                                    },
                                },
                            },
                        });
                        analytics.logEvent(
                            analytics.createSpaceEvent(space, {
                                type: 'space_create',
                                source: 'translation',
                                title,
                            })
                        );
                        return space;
                    });
                }}
                intent="space_creation"
            />
        </DialogRoot>
    );
}
